/* eslint-disable no-unused-vars */
import React, { useState, useMemo } from 'react';
import ProductoCard from './ProductoCard';
import logger from '../../../utils/logger';
import {
  CATEGORIAS,
  CATEGORIAS_PERMITIDAS,
  PRODUCTOS_PERMITIDOS,
  PRODUCTOS_SIN_BOTON,
} from '../../../constants';
import '../styles/Catalogo.css';

// Configuración de entorno
const esProduccion = process.env.NODE_ENV === 'production';
const debugMode = process.env.REACT_APP_DEBUG_PRODUCTOS === 'true';

// Usar constantes centralizadas
const categorias = CATEGORIAS;
const categoriasPermitidas = CATEGORIAS_PERMITIDAS;
const productosPermitidos = PRODUCTOS_PERMITIDOS;
const productosSinBoton = PRODUCTOS_SIN_BOTON;

// Función para capitalizar la primera letra y el resto en minúscula
const capitalizeFirstLabel = str => {
  const transformed = str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  return transformed;
};

// Función para formatear el slogan con la primera parte en negrita
const formatearSlogan = slogan => {
  if (!slogan) return { negrita: '', normal: '' };

  const primerPunto = slogan.indexOf('.');
  if (primerPunto === -1) {
    return { negrita: slogan, normal: '' };
  }

  const parteNegrita = slogan.substring(0, primerPunto + 1);
  const parteNormal = slogan.substring(primerPunto + 1).trim();

  return { negrita: parteNegrita, normal: parteNormal };
};

// Normaliza cadenas para rutas y claves
const normalize = str =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');

// Limpia el nombre del archivo para obtener un nombre presentable
const cleanNombre = filename =>
  filename
    .replace(/\.(png|jpe?g)$/i, '')
    .replace(/_/g, ' ') // Solo convertir guiones bajos a espacios
    // Mantener guiones (-) como están
    .replace(/\s+/g, ' ')
    .trim();

// Función para normalizar nombres de productos para comparación
const normalizarNombreProducto = nombre => {
  return nombre
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w]/g, '');
};

// Función para verificar si un producto debe ocultar el botón
const debeOcultarBoton = nombreProducto => {
  const nombreNormalizado = normalizarNombreProducto(nombreProducto);
  return productosSinBoton.some(producto =>
    nombreNormalizado.includes(normalizarNombreProducto(producto))
  );
};

// Función CORREGIDA para validar si un producto es permitido en producción
const esProductoPermitido = nombreProducto => {
  if (!esProduccion) return true; // En desarrollo, permitir todos

  // Comparación exacta de nombres (trim para quitar espacios extra)
  return productosPermitidos.includes(nombreProducto.trim());
};

// Función para validar si una imagen puede ser cargada
const validarImagenExiste = imgSrc => {
  try {
    return typeof imgSrc === 'string' && imgSrc.length > 0;
  } catch (error) {
    if (debugMode) logger.error('Error validando imagen:', error);
    return false;
  }
};

// Carga todas las imágenes de productos con validaciones
const requireProductos = require.context(
  '../../../assets/productos',
  true,
  /\.(png|jpe?g)$/i
);

// Importar las descripciones de productos
let descripcionesProductos = {};
try {
  descripcionesProductos =
    require('../../../utils/descripcionProductos.js').default || {};
} catch (error) {
  logger.warn('No se pudieron cargar las descripciones de productos:', error);
}

// Descripciones por defecto
const descripciones = [
  'Respaldo sin interrupciones ofreciendo alivio rápido',
  'Impulsa las defensas de tu sistema inmunológico',
  'Respaldo sin interrupciones ofreciendo alivio rápido',
  'Impulsa las defensas ofreciendo protección completa',
  'Respaldo sin interrupciones garantizando bienestar',
  'Impulsa las defensas de tu organismo efectivamente',
];

// PROCESAMIENTO SEGURO DE PRODUCTOS
const productosUnicos = new Map();
const productosPorCategoria = {};

// Función para crear clave única
const crearClaveUnica = (nombre, categoria) => {
  const nombreNormalizado = normalizarNombreProducto(nombre);
  const categoriaNormalizada = normalize(categoria);
  return `${categoriaNormalizada}_${nombreNormalizado}`;
};

// Debug inicial
// if (debugMode) {
//   console.log('=== DEBUG: Configuración de Productos ===');
//   console.log('Entorno:', esProduccion ? 'PRODUCCIÓN' : 'DESARROLLO');
//   console.log('Rutas encontradas:', requireProductos.keys().length);
//   console.log('Categorías permitidas:', categoriasPermitidas);
//   console.log('Productos permitidos:', productosPermitidos.length);
// }

// LOG TEMPORAL para extraer nombres exactos (quitar después del debug)
// console.log('=== NOMBRES EXACTOS ENCONTRADOS ===');
const nombresEncontrados = [];

// Procesamiento de productos con validaciones estrictas
requireProductos.keys().forEach((path, index) => {
  try {
    const partes = path.split('/');

    // Validación básica de estructura
    if (partes.length < 3) {
      if (debugMode) logger.warn(`❌ Estructura de ruta inválida: ${path}`);
      return;
    }

    const rawCategoria = decodeURIComponent(partes[1]);
    const categoria = normalize(rawCategoria);
    const nombreLimpio = cleanNombre(partes[2]);

    // LOG TEMPORAL: Agregar a la lista de nombres encontrados
    if (nombreLimpio && !nombresEncontrados.includes(nombreLimpio)) {
      nombresEncontrados.push(nombreLimpio);
    }

    // Validar categoría permitida
    if (esProduccion && !categoriasPermitidas.includes(categoria)) {
      if (debugMode)
        logger.warn(`❌ Categoría no permitida en producción: ${categoria}`);
      return;
    }

    // Validar nombre no vacío
    if (!nombreLimpio.trim()) {
      if (debugMode) logger.warn(`❌ Nombre vacío para: ${path}`);
      return;
    }

    // Validar producto permitido en producción
    if (!esProductoPermitido(nombreLimpio)) {
      if (debugMode)
        logger.warn(`❌ Producto no permitido en producción: ${nombreLimpio}`);
      return;
    }

    // Intentar cargar la imagen
    let imgSrc;
    try {
      imgSrc = requireProductos(path);
    } catch (error) {
      if (debugMode) logger.error(`❌ Error cargando imagen: ${path}`, error);
      return;
    }

    // Validar que la imagen existe
    if (!validarImagenExiste(imgSrc)) {
      if (debugMode) logger.warn(`❌ Imagen inválida: ${path}`);
      return;
    }

    const claveUnica = crearClaveUnica(nombreLimpio, categoria);

    // Verificar duplicados
    if (productosUnicos.has(claveUnica)) {
      if (debugMode)
        logger.warn(`❌ Duplicado omitido: "${nombreLimpio}" (${categoria})`);
      return;
    }

    // Inicializar categoría si no existe
    if (!productosPorCategoria[categoria]) {
      productosPorCategoria[categoria] = [];
    }

    const descripcionAleatoria =
      descripciones[Math.floor(Math.random() * descripciones.length)];

    // Buscar descripción del producto
    const claveProducto = Object.keys(descripcionesProductos).find(clave => {
      const producto = descripcionesProductos[clave];
      return (
        producto.nombre &&
        nombreLimpio
          .toLowerCase()
          .includes(producto.nombre.toLowerCase().replace(/\d+ml/i, '').trim())
      );
    });

    const principioActivo = claveProducto
      ? descripcionesProductos[claveProducto].principioActivo
      : '';
    const slogan = claveProducto
      ? descripcionesProductos[claveProducto].slogan
      : descripcionAleatoria;

    const productoData = {
      id: claveUnica,
      nombre: nombreLimpio,
      img: imgSrc,
      categoria,
      principioActivo,
      descripcion: slogan,
      rutaOriginal: path,
      indice: index,
      validado: true,
      // Info de debug solo en desarrollo
      ...(debugMode && {
        debugInfo: {
          procesadoEn: new Date().toISOString(),
          rawCategoria,
          claveProducto: claveProducto || 'no_encontrado',
        },
      }),
    };

    // Validación final antes de añadir
    if (productoData.img && productoData.nombre && productoData.categoria) {
      productosUnicos.set(claveUnica, productoData);
      productosPorCategoria[categoria].push(productoData);

      // if (debugMode) {
      //   console.log(`✅ Producto añadido: ${nombreLimpio} (${categoria})`);
      // }
    } else {
      // if (debugMode) console.warn(`❌ Validación final fallida: ${nombreLimpio}`);
    }
  } catch (error) {
    // console.error(`❌ Error procesando ${path}:`, error);
  }
});

// LOG TEMPORAL: Mostrar todos los nombres encontrados para verificar
// console.log('Nombres de productos encontrados:');
nombresEncontrados.sort().forEach(nombre => {
  const estaEnLista = productosPermitidos.includes(nombre);
  // console.log(`${estaEnLista ? '✅' : '❌'} '${nombre}',`);
});
// console.log('=== FIN DE LISTA ===');

// Log del resumen final
// logger.log('=== Resumen de Productos Cargados ===');
Object.keys(productosPorCategoria).forEach(categoria => {
  logger.log(
    `${categoria}: ${productosPorCategoria[categoria].length} productos`
  );
});
// console.log(`Total de productos únicos: ${productosUnicos.size}`);

// Exportar para debug (solo en desarrollo)
if (!esProduccion && typeof window !== 'undefined') {
  window.debugProductos = {
    productosUnicos,
    productosPorCategoria,
    rutasEncontradas: requireProductos.keys(),
    nombresEncontrados,
    configuracion: {
      esProduccion,
      debugMode,
      categoriasPermitidas,
      productosPermitidos: productosPermitidos.length,
    },
  };
  // console.log('📊 Debug data disponible en window.debugProductos');
}

const Catalogo = () => {
  const [categoriaActiva, setCategoriaActiva] = useState('');
  const [productoSeleccionadoNombre, setProductoSeleccionadoNombre] =
    useState(null);
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);

  const PRODUCTOS_POR_PAGINA = 9;

  const handleCategoriaClick = categoriaValue => {
    setCategoriaActiva(
      categoriaActiva === categoriaValue ? '' : categoriaValue
    );
    setPaginaActual(1);
  };

  const handleBuscar = () => {
    setPaginaActual(1);
  };

  const handleCloseModal = () => {
    setProductoSeleccionadoNombre(null);
  };

  const productosFiltrados = useMemo(() => {
    let productos = [];

    if (!categoriaActiva) {
      productos = Object.values(productosPorCategoria).flat();
    } else {
      productos = productosPorCategoria[categoriaActiva] || [];
    }

    if (terminoBusqueda.trim()) {
      productos = productos.filter(
        producto =>
          producto.nombre
            .toLowerCase()
            .includes(terminoBusqueda.toLowerCase()) ||
          producto.descripcion
            .toLowerCase()
            .includes(terminoBusqueda.toLowerCase()) ||
          (producto.principioActivo &&
            producto.principioActivo
              .toLowerCase()
              .includes(terminoBusqueda.toLowerCase()))
      );
    }

    // Filtro adicional: eliminar duplicados por ID único
    const productosDeduplicados = productos.filter((producto, index, array) => {
      return array.findIndex(p => p.id === producto.id) === index;
    });

    // Filtro final de seguridad en producción
    const productosFiltradosFinales = esProduccion
      ? productosDeduplicados.filter(
          producto => producto.validado && esProductoPermitido(producto.nombre)
        )
      : productosDeduplicados;

    return productosFiltradosFinales.sort((a, b) =>
      a.nombre.localeCompare(b.nombre, 'es', { numeric: true })
    );
  }, [categoriaActiva, terminoBusqueda]);

  const totalPaginas = Math.ceil(
    productosFiltrados.length / PRODUCTOS_POR_PAGINA
  );
  const indiceInicio = (paginaActual - 1) * PRODUCTOS_POR_PAGINA;
  const indiceFin = indiceInicio + PRODUCTOS_POR_PAGINA;
  const productosEnPagina = productosFiltrados.slice(indiceInicio, indiceFin);

  const handleCambiarPagina = nuevaPagina => {
    if (nuevaPagina >= 1 && nuevaPagina <= totalPaginas) {
      setPaginaActual(nuevaPagina);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="catalogo-container">
      <div className="catalogo-header">
        <div className="buscador-container">
          <div className="input-wrapper">
            <input
              type="text"
              className="buscador-input"
              placeholder="Buscar"
              value={terminoBusqueda}
              onChange={e => setTerminoBusqueda(e.target.value)}
            />
            <button className="buscar-icono" onClick={handleBuscar}>
              🔍
            </button>
          </div>
        </div>

        <div className="categorias-horizontales">
          {categorias.map(({ label, value }) => (
            <button
              key={value}
              className={`categoria-btn ${categoriaActiva === value ? 'active' : ''}`}
              onClick={() => handleCategoriaClick(value)}
              style={{ textTransform: 'none' }}
            >
              {capitalizeFirstLabel(label)}
            </button>
          ))}
        </div>
      </div>

      <div className="catalogo-grid">
        {productosEnPagina.map(prod => {
          const sloganFormateado = formatearSlogan(prod.descripcion);

          return (
            <div key={prod.id} className="catalogo-card">
              <img
                src={prod.img}
                loading="lazy"
                alt={prod.nombre}
                className="producto-img-catalago"
              />

              <h3 className="producto-nombre-catalago">{prod.nombre}</h3>

              <p className="producto-descripcion">
                {sloganFormateado.negrita && (
                  <strong>{sloganFormateado.negrita}</strong>
                )}
                {sloganFormateado.normal && (
                  <span> {sloganFormateado.normal}</span>
                )}
              </p>

              <p className="producto-info-adicional">{prod.infoAdicional}</p>

              {prod.categoria !== 'material-medico-descartable' &&
                !debeOcultarBoton(prod.nombre) && (
                  <div className="botones-container">
                    <button
                      className="btn-info"
                      onClick={() => setProductoSeleccionadoNombre(prod.nombre)}
                    >
                      Más información
                    </button>
                  </div>
                )}
            </div>
          );
        })}
      </div>

      {totalPaginas > 1 && (
        <div className="paginacion-container">
          <button
            className="paginacion-btn"
            onClick={() => handleCambiarPagina(paginaActual - 1)}
            disabled={paginaActual === 1}
          >
            ‹
          </button>

          {[...Array(Math.min(5, totalPaginas))].map((_, index) => {
            let numeroPagina;
            if (totalPaginas <= 5) {
              numeroPagina = index + 1;
            } else if (paginaActual <= 3) {
              numeroPagina = index + 1;
            } else if (paginaActual >= totalPaginas - 2) {
              numeroPagina = totalPaginas - 4 + index;
            } else {
              numeroPagina = paginaActual - 2 + index;
            }

            return (
              <button
                key={numeroPagina}
                className={`paginacion-btn ${paginaActual === numeroPagina ? 'active' : ''}`}
                onClick={() => handleCambiarPagina(numeroPagina)}
              >
                {numeroPagina}
              </button>
            );
          })}

          <button
            className="paginacion-btn"
            onClick={() => handleCambiarPagina(paginaActual + 1)}
            disabled={paginaActual === totalPaginas}
          >
            ›
          </button>
        </div>
      )}

      {productoSeleccionadoNombre && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-wrapper" onClick={e => e.stopPropagation()}>
            <ProductoCard
              productoNombre={productoSeleccionadoNombre}
              onClose={handleCloseModal}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalogo;
